import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

const MetaLayoutSubmissionContext = React.createContext(null);

export function MetaLayoutSubmissionContextProvider({ children }) {
  const idToHandlers = useRef({});
  const setSubmitFunction = useCallback(
    /**
     * @param id how to identify these specific handlers
     * @param onSubmit a function from meta forms which will be passed { rejectOnError: true }
     * @param onHighlight an optional function that will be called on the first submission with an error, allowing you to highlight it in some way to the user so that they can make the necessary adjustments
     */
    (id, onSubmit, onHighlight = () => null) => {
      idToHandlers.current[id] = { onSubmit, onHighlight };
    },
    [],
  );
  const removeSubmitFunction = useCallback((id) => {
    delete idToHandlers.current[id];
  }, []);
  const getHandlers = useCallback(() => {
    return idToHandlers.current;
  }, []);

  return (
    <MetaLayoutSubmissionContext.Provider value={{ setSubmitFunction, removeSubmitFunction, getHandlers }}>
      {children}
    </MetaLayoutSubmissionContext.Provider>
  );
}

MetaLayoutSubmissionContextProvider.defaultProps = {};

MetaLayoutSubmissionContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function withMetaLayoutSubmissionContext(Component, { scrollToFirstError = false } = {}) {
  return (props) => (
    <MetaLayoutSubmissionContextProvider scrollToFirstError={scrollToFirstError}>
      <Component {...props} />
    </MetaLayoutSubmissionContextProvider>
  );
}

export function useMetaLayoutSubmitAll() {
  const context = React.useContext(MetaLayoutSubmissionContext);

  if (!context) {
    throw new Error('useMetaLayoutSubmitAll must be called from a MetaLayoutSubmissionContextProvider');
  }

  const { getHandlers } = context;

  const submitAllSequentially = useCallback(async () => {
    let firstErr;
    for (const { onSubmit, onHighlight } of Object.values(getHandlers())) {
      try {
        await onSubmit({ rejectOnError: true }); // eslint-disable-line no-await-in-loop -- need the await in loop to do it one-by-one
      } catch (err) {
        if (!firstErr) {
          firstErr = err;
          onHighlight();
        }
      }
    }
    if (firstErr) {
      throw firstErr;
    }
  }, [getHandlers]);

  return {
    submitAllSequentially,
  };
}

// Allows adding functions to the submission context. This is barebones, if you want to have things like scrolling to a specific place you probably want MetaLayoutSubmissionAdder
export function useMetaLayoutSubmissionAdder(onSubmit, onHighlight) {
  const context = React.useContext(MetaLayoutSubmissionContext);

  if (!context) {
    throw new Error('useMetaLayoutSubmissionAdder must be called from a MetaLayoutSubmissionContextProvider');
  }

  const { setSubmitFunction, removeSubmitFunction } = context;
  // Set an ID once when the component first renders, so that when onSubmitOrReject changes we can remove the old one by ID and add the new one under the same ID.
  // Also helpful to clean up onSubmitOrReject functions for resources that get removed
  const [id] = useState(uuid());

  useEffect(() => {
    if (onSubmit) {
      setSubmitFunction(id, onSubmit, onHighlight);
    } else {
      removeSubmitFunction(id);
    }

    return () => removeSubmitFunction(id);
  }, [onSubmit, onHighlight, setSubmitFunction, id, removeSubmitFunction]);
}
